import { useMemo } from "react";
import { Dayjs } from "dayjs";
import getCheckOutDate from "../configs/checkoutDateRules";
import { useAppSelector } from "./reduxHooks";

const defaultWorkingDays = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const parseTime = (time: string) => {
  const [hours, minutes, seconds] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, seconds, 0);
  return date;
};

export interface IDateHookResult {
  /**
   * Best-known date. While store config hasn't loaded yet this is derived
   * from hardcoded defaults (a guess) - check `isReady` before treating it
   * as authoritative.
   */
  date: Dayjs;
  /**
   * True once the store config request has settled (succeeded or failed).
   * Only then is `date` derived from real config (or, if the request
   * failed, from the hardcoded defaults used as a genuine last resort).
   * False while the config is still in flight - callers must not treat
   * `date` as final in that state.
   */
  isReady: boolean;
}

export const useSpecialDate = (): IDateHookResult => {
  const storeConfig = useAppSelector(
    (state) => state.store.getStoreConfigResponse
  );
  const storeConfigError = useAppSelector(
    (state) => state.store.getStoreConfigError
  );

  const isReady = !!storeConfig || !!storeConfigError;

  const specialDate = useMemo(() => {
    const body = storeConfig?.body;

    return getCheckOutDate({
      isSpecialDay: true,
      checkInCutOffTime: parseTime(body?.cutOffTime || "10:00:00"),
      pickupTime: parseTime(body?.pickupTime || "15:00:00"),
      workingDays: body?.workingDays || defaultWorkingDays,
      generalServiceDays: body?.daysToDeliver ?? 2,
      specialServiceDays: body?.specialDaysToDeliver ?? 0,
    });
  }, [storeConfig]);

  return { date: specialDate, isReady };
};
